import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { TagChip } from "../../TagChip";
import { useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import HeartIcon from "@mui/icons-material/FavoriteBorder";
import HeartFilledIcon from "@mui/icons-material/Favorite";

export type DesignersPersonaCardProps = {
  title: string;
  subtitle: string;
  description: string;
  image: string;
  age: string;
  tags: string[];
};

export const DesignersPersonaCard = ({
  title,
  subtitle,
  description,
  image,
  age,
  tags,
}: DesignersPersonaCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);

  const imageMaxHeight = expanded ? "160px" : "433px";
  const transitionTiming = "0.3s ease-in-out";

  const cardWidth = {
    xs: "320px",
    sm: "360px",
  };

  return (
    <Paper
      elevation={3}
      sx={{
        overflow: "hidden",
        maxWidth: cardWidth,
        minWidth: cardWidth,
        cursor: "pointer",
      }}
      onClick={() => setExpanded(!expanded)}
    >
      <Box
        className="image-container text-center"
        sx={{
          maxHeight: imageMaxHeight,
          overflow: "hidden",
          transition: `max-height ${transitionTiming}`,
          position: "relative",
        }}
      >
        <img
          src={image}
          alt={title}
          style={{
            maxHeight: "433px",
            width: "auto",
            transform: `translateY(${expanded ? "-8%" : "0%"}) scale(${
              expanded ? 1.1 : 1
            })`,
            transition: `transform ${transitionTiming}`,
          }}
        />
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            setIsFavorite(!isFavorite);
          }}
          className="fav-button"
          color={"white" as any}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            left: "auto",
            background: "#fff",
            color: "#000",
            "&:hover": {
              background: "#fff",
            },
          }}
        >
          {isFavorite ? <HeartFilledIcon /> : <HeartIcon />}
        </IconButton>
      </Box>

      <Box className="content-container" sx={{ p: 2, background: "#fff" }}>
        <Stack spacing={1.5}>
          <Typography variant="h5" component="h6" fontWeight={600}>
            {title}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              fontSize: {
                xs: "0.8rem",
                sm: "0.9rem",
              },
            }}
          >
            {subtitle}
          </Typography>
          <Stack direction="row" spacing={1}>
            <TagChip
              sx={{
                whiteSpace: "nowrap",
                fontSize: {
                  xs: "0.8rem",
                  sm: "0.9rem",
                },
              }}
            >
              Age: {age}
            </TagChip>
            {tags.map((tag) => (
              <TagChip
                key={tag}
                sx={{
                  whiteSpace: "nowrap",
                  fontSize: {
                    xs: "0.8rem",
                    sm: "0.9rem",
                  },
                }}
              >
                {tag}
              </TagChip>
            ))}
          </Stack>
          <Box
            className="description-container"
            sx={{
              overflow: {
                xs: "auto",
                sm: "hidden",
              },
              height: expanded ? "270px" : "0px",
              transition: `height ${transitionTiming}`,
            }}
          >
            {description.split(/\n+/).map((line, idx) => (
              <Typography
                key={idx}
                variant="body1"
                fontSize={{
                  xs: "14px",
                }}
                component="p"
                sx={{ mb: 1 }}
              >
                {line}
              </Typography>
            ))}
          </Box>
        </Stack>

        <Button
          sx={{
            width: "100%",
            mt: 1.5,
          }}
          variant={expanded ? "contained" : "outlined"}
          onClick={() => setExpanded(!expanded)}
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          <Typography variant="body1">
            {expanded ? "Go Back" : "Show More"}
          </Typography>
        </Button>
      </Box>
    </Paper>
  );
};
